import { Code, Globe, ExternalLink, GitBranch, Server } from "lucide-react";
import SectionHeading from "./ui/SectionHeading";
import Reveal, { staggerDelay } from "./ui/Reveal";
import FlagshipProducts from "./FlagshipProducts";

const projectsData = [
  {
    title: "EduSpark Platform",
    type: "Full Stack Application",
    description: "Developed an educational platform bridging students and course creators, featuring secure material sharing and interactive online course accessibility.",
    technologies: ["React.js", "Node.js", "Express", "MySQL", "JWT"],
    links: {
      github: "https://github.com/razan-aboushi/Edu_Spark",
    },
    icon: Globe,
    featured: true,
  },
  {
    title: "TableXpress",
    type: "Full Stack Application",
    description: "Built a full-stack restaurant reservation system incorporating location-based filtering, cuisine classification, and real-time availability tracking.",
    technologies: ["React.js", "Node.js", "Tailwind CSS", "Bootstrap", "MySQL"],
    links: {
      github: "https://github.com/razan-aboushi/tablexpress",
    },
    icon: Globe,
    featured: true,
  },
  {
    title: "Task Manager with Zustand",
    type: "React Application",
    description: "Built a modern task management application using React with Zustand for state management, featuring add, edit, delete, and complete task functionalities.",
    technologies: ["React", "Zustand", "JavaScript"],
    links: {
      github: "https://github.com/razan-aboushi/Add-Tasks-with-Zustand",
    },
    icon: Code,
  },
  {
    title: "YouBelong Platform",
    type: "Full Stack Application",
    description: "A comprehensive web platform built using Laravel framework following MVC architecture pattern with MySQL database integration.",
    technologies: ["PHP 8", "Laravel Framework 9", "MySQL 5.7", "HTML", "CSS", "Bootstrap", "Apache Server", "Composer"],
    links: {
      github: "https://github.com/razan-aboushi/youbelong",
    },
    icon: Globe,
    featured: true,
  },
  {
    title: "Expense Tracker",
    type: "JavaScript Application",
    description: "Developed a comprehensive expense tracking application with budget management, expense categorization, and financial reporting features.",
    technologies: ["JavaScript", "HTML5", "CSS3", "Local Storage"],
    links: {
      github: "https://github.com/razan-aboushi/Expense-Tracker",
    },
    icon: Code,
  },
  {
    title: "Memory Game",
    type: "JavaScript Game",
    description: "Created an interactive memory card game with flip animations, score tracking, and difficulty levels using pure JavaScript.",
    technologies: ["JavaScript", "HTML5", "CSS3", "Animations"],
    links: {
      github: "https://github.com/razan-aboushi/Memory-Game",
    },
    icon: Code,
  },
  {
    title: "Snake Game",
    type: "JavaScript Game",
    description: "Implemented the classic Snake game with smooth controls, score tracking, and increasing difficulty levels.",
    technologies: ["JavaScript", "HTML5", "Canvas"],
    links: {
      github: "https://github.com/razan-aboushi/Snake-game",
    },
    icon: Code,
  },
  {
    title: "2D Breakout Game",
    type: "JavaScript Game",
    description: "Built a 2D breakout game with paddle controls, brick physics, power-ups, and progressive difficulty levels.",
    technologies: ["JavaScript", "Canvas", "Game Physics"],
    links: {
      github: "https://github.com/razan-aboushi/2D-breakout-game",
    },
    icon: Code,
  },
  {
    title: "Quiz Application",
    type: "JavaScript Application",
    description: "Created an interactive quiz application with multiple categories, timer functionality, and score tracking system.",
    technologies: ["JavaScript", "HTML5", "CSS3"],
    links: {
      github: "https://github.com/razan-aboushi/Quiz-by-JavaScript",
    },
    icon: Code,
  },
  {
    title: "Chat Rooms",
    type: "Node.js Application",
    description: "Developed real-time chat rooms with Socket.io, user authentication, and message history functionality.",
    technologies: ["Node.js", "Socket.io", "Express"],
    links: {
      github: "https://github.com/razan-aboushi/chat-rooms",
    },
    icon: Server,
    featured: true,
  },
  {
    title: "Simple NodeJS",
    type: "Backend Application",
    description: "Created a basic Node.js server with RESTful API endpoints, middleware implementation, and error handling.",
    technologies: ["Node.js", "Express", "REST API"],
    links: {
      github: "https://github.com/razan-aboushi/Simple_NodeJS",
    },
    icon: Server,
  },
  {
    title: "Mockup and Wireframe",
    type: "Design Project",
    description: "Created initial website wireframes and mockup screens using Figma for UI/UX design planning and prototyping.",
    technologies: ["Figma", "Wireframing", "UI Design", "Prototyping"],
    links: {
      github: "https://github.com/razan-aboushi/mockup-and-wireframe",
    },
    icon: Globe,
  },
];

const featuredProjects = projectsData.filter(p => p.featured);
const standardProjects = projectsData.filter(p => !p.featured);

export default function ProjectsElegant() {
  return (
    <section id="projects" className="relative py-24 md:py-28 bg-[#0a0a0a]">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHeading
          eyebrow="Projects"
          title="Featured"
          highlight="Work"
          description="Full-stack applications, real-time systems, and experiments I've designed and built end to end"
        />

        <FlagshipProducts />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {featuredProjects.map((project, index) => {
            const Icon = project.icon;
            return (
              <Reveal key={project.title} delay={staggerDelay(index)} className="h-full">
              <div
                className="spotlight group relative flex h-full flex-col justify-between p-6 sm:p-8 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-purple-500/50 hover:bg-white/[0.06] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div className="p-3 bg-white/5 rounded-xl text-purple-400 border border-white/10">
                      <Icon size={24} strokeWidth={1.5} />
                    </div>
                    <span className="text-xs font-medium px-3 py-1 bg-purple-500/10 text-purple-300 rounded-full border border-purple-500/20">
                      {project.type}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-blue-400 transition-colors">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-8">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2.5 py-1 bg-[#0a0a0a] text-gray-300 rounded-md text-xs border border-gray-800"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="relative z-10 flex gap-4 mt-auto">
                  {project.links.github && (
                    <a
                      href={project.links.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm font-medium text-white px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    >
                      <GitBranch size={16} aria-hidden="true" />
                      Source Code
                      <span className="sr-only"> for {project.title} on GitHub</span>
                    </a>
                  )}
                </div>
              </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Other Projects & Games</h3>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {standardProjects.map((project, index) => (
            <Reveal key={project.title} delay={staggerDelay(index % 4)} className="h-full">
              {/* Only the title is the link (short, descriptive anchor text); its ::after stretches over the card so the whole card stays clickable. */}
              <article className="spotlight p-5 rounded-xl bg-white/[0.04] border border-white/10 hover:border-purple-500/40 hover:bg-white/[0.06] hover:-translate-y-1 focus-within:ring-2 focus-within:ring-purple-400/70 transition-all duration-300 flex flex-col h-full group">
                <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-300 transition-colors">
                  <a
                    href={project.links.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="after:absolute after:inset-0 after:rounded-xl after:content-[''] focus-visible:outline-none"
                  >
                    {project.title}
                    <span className="sr-only"> — source on GitHub</span>
                  </a>
                </h4>
                <p className="text-sm text-gray-400 mb-4 flex-grow line-clamp-3">{project.description}</p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
                  <span className="text-xs text-gray-300 px-2 py-0.5 rounded-md bg-white/5 border border-white/10">{project.technologies[0]}</span>
                  <GitBranch size={18} className="text-gray-400 group-hover:text-white transition-colors" aria-hidden="true" />
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="flex justify-center">
          <a
            href="https://github.com/razan-aboushi"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-white/5 text-white rounded-full font-semibold border border-white/10 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300"
          >
            <GitBranch size={20} className="group-hover:text-purple-400 transition-colors" />
            <span className="text-sm sm:text-base whitespace-nowrap">View All 39+ Projects on GitHub</span>
            <ExternalLink size={16} className="text-gray-500 group-hover:text-white transition-colors" />
          </a>
        </div>

      </div>
    </section>
  );
}